import { Alert, Box, Button } from '@ksyos/design-system';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { imageAndPdfExtensions } from '@common/utils/fileExtensions';
import type { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import type { IAsset } from '../../../../interfaces/asset';
import type { IFileUpload } from '../../../../interfaces/fileUpload';
import type { IProcess } from '../../../../interfaces/process';
import type { IServiceGetOne } from '../../../../interfaces/serviceGetOne';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { ToastType } from '../../../../../../common/enums/toastType';
import { attachmentLimit } from '../../../../../../common/validation/asset';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { showToast } from '../../../../../utils/showToast';
import { AssetDropzone } from '../../../../elements/AssetDropzone';
import { HeaderDivider, IconColor, InputCheckbox, LinkOnClick, Loader } from '../../../../elements/common';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { UploadIcon } from '../../../../elements/Icons/uploadIcon/UploadIcon';
import { List, ListItem } from '../../../../elements/List/List';
import { MediaObject } from '../../../../elements/mediaObject/MediaObject';
import { Modal } from '../../../../elements/modals/Modal';
import { RenderAssetThumbnails } from '../../../episodes/common';
import { ExistingExaminationModal } from './ExistingExaminationModal';

interface IAssetDropzoneWithActionsProps {
    acceptedFiles?: string;
    additionalFilesCheckboxText?: string;
    assets: IAsset[];
    assetsBaseUrl: FilesUrlBase;
    automation?: string;
    cleanDropzoneAfterCompletingFiles?: boolean;
    extraActions?: IExtraAction[];
    group?: string;
    headerText?: string;
    ksyosDesktopActionLabels?: IKsyosDesktopActionLabels;
    maxFiles?: number;
    meetsCustomAssetValidation?: (file: IFileUpload) => boolean;
    metadata?: IUploadMetadata;
    onAction?: (actionId: string) => void;
    onChange: (assets: IAsset[]) => Promise<void>;
    onClickStartKsyosDesktop?: () => Promise<void>;
    onFileAccepted?: () => Promise<void>;
    onUploading: (isUploading: boolean) => Promise<void>;
    placeholder?: string;
    processActivityId?: string;
    service: IServiceGetOne;
    showAdditionalFilesCheckbox?: boolean;
    showUseExistingExamination?: boolean;
    updateNewRequestPreviousRequest?: (previousRequestId: string) => Promise<void>;
}

interface IDropzoneActions {
    items: JSX.Element[];
    title?: string;
}

export interface IKsyosDesktopActionLabels {
    linkTextWhenMultipleActions: string;
    linkTextWhenOnlyAction: string;
    titleWhenOnlyAction: string;
}

export interface IUploadMetadata {
    placement?: string;
}

interface IExtraAction {
    actionId: string;
    disabled?: boolean;
    icon: string;
    label: string;
}

export function AssetDropzoneWithActions(props: IAssetDropzoneWithActionsProps) {
    const { patient } = useEpisodeContext();
    const [potentialPreviousRequests, setPotentialPreviousRequests] = useState<IProcess[]>([]);
    const [totalPotentialPreviousRequests, setTotalPotentialPreviousRequests] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isExaminationModalOpen, setIsExaminationModalOpen] = useState(false);
    const [isPreviousExaminationSelected, setIsPreviousExaminationSelected] = useState(false);
    const [showDropzone, setShowDropzone] = useState(!props.showAdditionalFilesCheckbox);
    const [showCancelExtraAssetsModal, setShowCancelExtraAssetsModal] = useState(false);
    const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<string | undefined>();
    const [dropzoneActions, setDropzoneActions] = useState<IDropzoneActions | undefined>();
    const [group] = useState<string>(props.group || uuidv4());
    const [isCompletingFiles, setIsCompletingFiles] = useState(false);

    useEffect(() => {
        if (
            [ServiceType.consultation, ServiceType.referral, ServiceType.referralSmall].includes(
                props.service.serviceType
            )
        ) {
            fetchPotentialPreviousRequests();
        }
    }, [patient.id, props.service.id]);

    useEffect(() => {
        determineActions();
    }, [props.ksyosDesktopActionLabels, potentialPreviousRequests, isPreviousExaminationSelected]);

    if (!isLoading) {
        return (
            <>
                {props.cleanDropzoneAfterCompletingFiles && isCompletingFiles && (
                    <Loader
                        fixedCentered
                        isBlocking
                    />
                )}

                {props.headerText && <HeaderDivider text={props.headerText} />}

                {fileUploadErrorMessage && (
                    <Alert appearance="warning">{`${translateKey(
                        'dropzone.uploadError'
                    )}: ${fileUploadErrorMessage}`}</Alert>
                )}

                <Modal
                    actionButtons={[
                        <Button
                            onClick={() => setShowCancelExtraAssetsModal(false)}
                            variant="outline"
                        >
                            {translateKey('common.no')}
                        </Button>,
                        <Button
                            onClick={() => {
                                setShowCancelExtraAssetsModal(false);
                                setShowDropzone(false);
                                props.onChange([]);
                            }}
                        >
                            {translateKey('common.yes')}
                        </Button>,
                    ]}
                    isOpen={showCancelExtraAssetsModal}
                    onClose={() => setShowCancelExtraAssetsModal(false)}
                    size="small"
                    title={translateKey('procedure.sureToCancelAttachments')}
                ></Modal>

                {props.showAdditionalFilesCheckbox && (
                    <FormGroup>
                        <InputCheckbox
                            automation="addExtraFiles"
                            checked={showDropzone}
                            onChange={async (value) => await onChangeShowDropzone(value)}
                            text={props.additionalFilesCheckboxText || translateKey('procedure.sendExtraAttachments')}
                        />
                    </FormGroup>
                )}

                {showDropzone && renderAssetDropzoneWithActions()}

                {potentialPreviousRequests.length > 0 && props.updateNewRequestPreviousRequest && (
                    <ExistingExaminationModal
                        handleClose={(isSuccess?: boolean) => {
                            setIsExaminationModalOpen(false);
                            if (isSuccess) {
                                setIsPreviousExaminationSelected(true);
                                showToast(ToastType.success, translateKey('toast.convertConsult'));
                            }
                        }}
                        initialPotentialPreviousRequests={potentialPreviousRequests}
                        initialTotalPotentialPreviousRequests={totalPotentialPreviousRequests}
                        isOpen={isExaminationModalOpen}
                        patientId={patient.id}
                        serviceId={props.service.id}
                        serviceType={props.service.serviceType}
                        updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                    />
                )}

                {props.assets.filter((file) => file.id !== undefined).length > 0 && props.processActivityId && (
                    <FormGroup>
                        <FormLabel>{translateKey('request.attachment')}</FormLabel>
                        <RenderAssetThumbnails
                            assets={props.assets}
                            group={group}
                            id={props.processActivityId}
                            onRemove={async (id) => {
                                setIsCompletingFiles(true);
                                await props.onChange([...props.assets].filter((file) => file.id !== id));
                                setIsCompletingFiles(false);
                            }}
                            urlBase={props.assetsBaseUrl}
                        />
                    </FormGroup>
                )}
            </>
        );
    } else {
        return null;
    }

    function renderAssetDropzoneWithActions() {
        if (
            (dropzoneActions && dropzoneActions.items.length > 0) ||
            (props.extraActions && props.extraActions.length > 0)
        ) {
            return (
                <Box display={['block', null, 'flex']}>
                    <Box
                        display="flex"
                        justifyContent="center"
                    >
                        {renderAssetDropzone()}
                    </Box>
                    <Box
                        display="flex"
                        justifyContent="center"
                    >
                        {dropzoneActions && dropzoneActions.items.length > 0 && (
                            <MediaObject
                                mediaItem={<UploadIcon icon="examination" />}
                                title={dropzoneActions.title}
                            >
                                <List>{dropzoneActions.items}</List>
                            </MediaObject>
                        )}
                        {props.extraActions &&
                            props.extraActions.map((extraAction) => (
                                <MediaObject
                                    key={extraAction.actionId}
                                    mediaItem={
                                        <UploadIcon
                                            color={extraAction.disabled ? IconColor.muted : undefined}
                                            icon={extraAction.icon}
                                        />
                                    }
                                >
                                    <LinkOnClick
                                        className={`automation-${extraAction.actionId}`}
                                        disabled={extraAction.disabled}
                                        onClick={async () => {
                                            if (props.onAction) {
                                                props.onAction(extraAction.actionId);
                                            }
                                        }}
                                    >
                                        {extraAction.label}
                                    </LinkOnClick>
                                </MediaObject>
                            ))}
                    </Box>
                </Box>
            );
        } else {
            return renderAssetDropzone();
        }
    }

    function renderAssetDropzone() {
        return (
            <AssetDropzone
                acceptedFiles={props.acceptedFiles || imageAndPdfExtensions}
                baseUrl={props.assetsBaseUrl}
                className={classnames(props.automation ? 'automation-' + props.automation : '')}
                cleanDropzoneAfterCompletingFiles={props.cleanDropzoneAfterCompletingFiles}
                group={group}
                itemId={props.processActivityId}
                maxFiles={
                    (props.maxFiles || attachmentLimit) - props.assets.filter((file) => file.id !== undefined).length
                }
                meetsCustomAssetValidation={props.meetsCustomAssetValidation}
                metadata={props.metadata}
                numberOfSavedAssets={props.assets.filter((asset) => asset.s3Id === undefined).length}
                numberOfUploadingAssets={props.assets.filter((asset) => asset.s3Id !== undefined).length}
                onCompletingFiles={(value) => setIsCompletingFiles(value)}
                onError={(errorMessage) => setFileUploadErrorMessage(errorMessage)}
                onFileAccepted={async () => {
                    if (props.onFileAccepted) {
                        await props.onFileAccepted();
                    }
                }}
                onQueueComplete={async (files, anAddedFileUploadFailed) =>
                    await onFileUploadCompleted(files, anAddedFileUploadFailed)
                }
                onRemoved={async (file) => await removeAsset(file.s3Id)}
                onSending={async (fileUploadFailed) => {
                    await props.onUploading(true);
                    if (!fileUploadFailed) {
                        setFileUploadErrorMessage(undefined);
                    }
                }}
                placeholder={props.placeholder}
                setMaxAmountFiles={props.maxFiles || attachmentLimit}
            />
        );
    }

    async function fetchPotentialPreviousRequests() {
        const result = await getJson(
            `/api/requestProcesses?patientId=${patient.id}&serviceIdForPotentialPreviousRequests=${props.service.id}&limit=3&orderByCreatedAtDesc=true`
        );
        setPotentialPreviousRequests(result.json.items);
        setTotalPotentialPreviousRequests(result.json.count);
    }

    async function determineActions() {
        const items = [];
        const showUseExistingExamination =
            !props.showAdditionalFilesCheckbox &&
            potentialPreviousRequests &&
            potentialPreviousRequests.length > 0 &&
            !isPreviousExaminationSelected &&
            props.updateNewRequestPreviousRequest &&
            props.showUseExistingExamination !== false;

        const showStartKsyosDesktop = props.onClickStartKsyosDesktop !== undefined && props.ksyosDesktopActionLabels;
        const title =
            !showUseExistingExamination && showStartKsyosDesktop && props.ksyosDesktopActionLabels
                ? props.ksyosDesktopActionLabels.titleWhenOnlyAction
                : undefined;

        if (showUseExistingExamination) {
            const or = showStartKsyosDesktop ? ` ${translateKey('common.or')}` : '';
            const linkText =
                props.service.serviceType === ServiceType.consultation
                    ? translateKey('common.useExistingExamination')
                    : translateKey('common.useExistingExaminationOrConsult');
            items.push(
                <ListItem key={`item${items.length}`}>
                    <LinkOnClick onClick={async () => setIsExaminationModalOpen(true)}>{linkText}</LinkOnClick>
                    {or}
                </ListItem>
            );
        }

        if (showStartKsyosDesktop && props.ksyosDesktopActionLabels) {
            const linkText =
                items.length > 0
                    ? props.ksyosDesktopActionLabels.linkTextWhenMultipleActions
                    : props.ksyosDesktopActionLabels.linkTextWhenOnlyAction;
            items.push(
                <ListItem key={`item${items.length}`}>
                    <LinkOnClick
                        onClick={async () => {
                            await props.onClickStartKsyosDesktop!();
                        }}
                    >
                        {linkText}
                    </LinkOnClick>
                </ListItem>
            );
        }

        setDropzoneActions({ items, title });
        setIsLoading(false);
    }

    function onChangeShowDropzone(value: boolean) {
        if (!value && props.assets.length > 0) {
            setShowCancelExtraAssetsModal(true);
        } else {
            setShowDropzone(value);
        }
    }

    async function onFileUploadCompleted(files?: IFileUpload[], anAddedFileUploadFailed?: boolean) {
        if (files) {
            const newAssets = files.map((file) => {
                return { filename: file.name, s3Id: file.s3Id };
            });

            await props.onChange([...props.assets, ...newAssets]);

            if (!anAddedFileUploadFailed) {
                setFileUploadErrorMessage(undefined);
            }
        }

        await props.onUploading(false);
    }

    async function removeAsset(s3Id: string) {
        await props.onChange([...props.assets].filter((file) => !file.s3Id || file.s3Id !== s3Id));
    }
}
